import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackCollectionCover } from "@/lib/catalog/fallback-images";
import { corporatePacksQuery } from "@/lib/corporate/queries";
import { formatPaise } from "@/lib/corporate/format";
import { canonicalUrl } from "@/lib/seo/site";

export const Route = createFileRoute("/corporate/")({
  head: () => ({
    meta: [
      { title: "Corporate Gifting & Bulk Orders — CB16 Atelier" },
      {
        name: "description",
        content:
          "Bulk fragrance gifting for teams, clients and events. Tiered pricing, GST invoicing, custom branding and multi-address delivery across India.",
      },
      { property: "og:title", content: "Corporate Gifting & Bulk Orders — CB16 Atelier" },
      {
        property: "og:description",
        content: "Tiered bulk pricing, GST invoicing, branding and multi-address delivery for corporate fragrance gifting.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: canonicalUrl("/corporate") },
    ],
    links: [{ rel: "canonical", href: canonicalUrl("/corporate") }],
  }),
  loader: ({ context }) => {
    void context.queryClient.ensureQueryData(corporatePacksQuery());
  },
  component: CorporateLanding,
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>Corporate desk unavailable</h1>
      <p className="mt-3 text-sm text-black/60">Please try again shortly, or write to us and we will respond directly.</p>
    </div>
  ),
});

const BENEFITS = [
  { title: "Tiered bulk pricing", body: "Unit pricing falls automatically as volume rises. Every tier is published up front." },
  { title: "GST compliant", body: "GSTIN captured at quote stage; tax invoice issued against your purchase order." },
  { title: "Custom branding", body: "Logo-embossed sleeves, printed greeting cards and bespoke ribbon on eligible packs." },
  { title: "Multi-address delivery", body: "Split a single order across offices, teams or client addresses nationwide." },
];

function CorporateLanding() {
  const { data } = useSuspenseQuery(corporatePacksQuery());
  const packs = data.packs;

  return (
    <div className="bg-white text-black" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="border-b border-black/10 bg-[#faf7f2]">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <p className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">Corporate</p>
          <h1 className="mt-4 max-w-3xl text-4xl leading-tight sm:text-5xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
            Fragrance gifting, at the scale your business works
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-black/65">
            Curated bulk packs for employee recognition, client gifting, festivals and events — with published tier
            pricing, GST invoicing and delivery split across as many addresses as you need.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href="#packs"
              className="border border-black bg-black px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-black/85"
            >
              Browse corporate packs
            </a>
            <Link
              to="/contact"
              className="border border-black/25 px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] transition hover:border-black"
            >
              Contact sales
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((b) => (
            <div key={b.title} className="border-t border-[#C6A15B]/50 pt-5">
              <h2 className="text-lg" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>{b.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-black/60">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="packs" className="border-t border-black/10 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <h2 className="text-3xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>Corporate pack catalogue</h2>
          <p className="mt-2 text-sm text-black/55">Minimum order quantities and volume tiers are shown on each pack.</p>

          {data.error && <p className="mt-8 text-sm text-red-700">{data.error}</p>}
          {!data.error && packs.length === 0 && (
            <p className="mt-8 text-sm text-black/55">No corporate packs are published yet. Please contact our sales desk.</p>
          )}

          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {packs.map((p) => (
              <Link
                key={p.slug}
                to="/corporate/$slug"
                params={{ slug: p.slug }}
                className="group flex flex-col border border-black/10 transition hover:border-black/30"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#f4f1ec]">
                  <SafeImage
                    src={p.thumbnailImage ?? p.heroImage}
                    fallback={fallbackCollectionCover(p.slug)}
                    alt={p.name}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="text-xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>{p.name}</h3>
                  {p.shortDescription && <p className="mt-2 line-clamp-2 text-sm text-black/55">{p.shortDescription}</p>}
                  <dl className="mt-4 space-y-1 text-[12px] text-black/60">
                    <div className="flex justify-between"><dt>Minimum order</dt><dd className="font-medium text-black">{p.minOrderQty} units</dd></div>
                    <div className="flex justify-between"><dt>Bottle size</dt><dd className="font-medium text-black">{p.bottleSizeMl} ML</dd></div>
                    {p.fromUnitPriceCents ? (
                      <div className="flex justify-between"><dt>From</dt><dd className="font-medium text-black">{formatPaise(p.fromUnitPriceCents)} / unit</dd></div>
                    ) : null}
                    <div className="flex justify-between"><dt>Volume tiers</dt><dd className="font-medium text-black">{p.tiers.length || "—"}</dd></div>
                  </dl>
                  <span className="mt-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C6A15B]">
                    {p.requiresQuote ? "Request a quote" : "View pricing"} →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}